import { useCallback, useEffect, useState } from 'react';
import { aroApi, MODE_LABELS } from './api.js';

const usd = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const mm = (v) => (v == null ? '—' : `$${v.toFixed(2)}MM`);
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

function SourceTag({ label }) {
  const cls = /D2D/.test(label) ? 'd2d' : /seller/.test(label) ? 'seller' : /quote/.test(label) ? 'quote' : 'user';
  return <span className={`aro-src aro-src-${cls}`} data-testid="aro-src">{label}</span>;
}

/**
 * Configurable ARO: mode, totals, sourced assumptions, per-well values with
 * source labels, deal / well overrides, audit trail. ``onChange`` fires after
 * any saved change so the caller can re-price (Financials / ABS).
 */
export default function AroPanel({ dealId, onChange }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [wellForm, setWellForm] = useState(null);
  const [dealForm, setDealForm] = useState({ kind: 'lumpSum', value: '' });

  useEffect(() => {
    let alive = true;
    setView(null);
    setErr(null);
    aroApi.deal(dealId).then((v) => alive && setView(v)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [dealId]);

  const save = useCallback(async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      const v = await fn();
      setView(v);
      onChange?.(v);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, [onChange]);

  if (err && !view) return <div className="abs-aro-banner" role="status" data-testid="aro-error">ARO unavailable: {err}</div>;
  if (!view) return <div className="aw-small" data-testid="aro-loading">Loading ARO…</div>;
  const r = view.resolved;
  const cfg = view.config || {};
  const t = r.totals || {};
  const dealOv = cfg.dealOverride;

  return (
    <section className="abs-section aro-panel" data-testid="aro-panel">
      <h3>ARO (plugging &amp; abandonment)</h3>
      <div className="aro-head">
        <label className="aw-small">
          Treatment{' '}
          <select
            value={cfg.mode || 'd2d'}
            disabled={busy}
            data-testid="aro-mode"
            onChange={(e) => save(() => aroApi.setConfig(dealId, { ...cfg, mode: e.target.value }))}
          >
            {Object.entries(MODE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <SourceTag label={r.sourceLabel} />
        {cfg.updatedBy && <span className="aw-small"> · set by {cfg.updatedBy} {cfg.updatedAt}</span>}
      </div>
      <div className="aro-kpis" data-testid="aro-kpis">
        <div><b>{mm(t.netAtPaMM)}</b><span>Net ARO at P&amp;A (ABS haircut)</span></div>
        <div><b>{mm(t.pvMM)}</b><span>ARO PV @ {(r.discountRate * 100).toFixed(0)}% (in deal PV)</span></div>
        <div><b>{pct(t.aroPctPdpPv)}</b><span>ARO PV / PDP PV10</span></div>
        <div><b>{t.wellCount}</b><span>wellbores priced</span></div>
      </div>
      {!r.known && (
        <div className="abs-aro-banner" role="status" data-testid="aro-unknown">
          No ARO estimate or override — ABS note sizing stays blocked (aro_unknown).
        </div>
      )}
      {r.placeholders?.length > 0 && (
        <p className="aw-small aro-ph" data-testid="aro-placeholders">
          Placeholder — set me: {r.placeholders.join(', ')} (treated as 0 until set; no public source found).
        </p>
      )}
      <button type="button" className="fin-pill" onClick={() => setOpen((o) => !o)} data-testid="aro-toggle">
        {open ? 'Hide' : 'Show'} assumptions, wells &amp; audit
      </button>
      {open && (
        <>
          <h4>Deal override</h4>
          <div className="aro-dealov" data-testid="aro-dealov">
            {dealOv ? (
              <span className="aw-small">
                Active: {dealOv.kind === 'lumpSum' ? `lump sum ${mm(dealOv.amountMM)}` : `${usd(dealOv.amount)}/well gross`}
                {dealOv.provenance ? ` · ${dealOv.provenance}` : ''}{' '}
                <button type="button" disabled={busy} onClick={() => save(() => aroApi.setConfig(dealId, { mode: cfg.mode || 'd2d' }))}>Clear</button>
              </span>
            ) : (
              <span className="aw-small">
                <select value={dealForm.kind} onChange={(e) => setDealForm({ ...dealForm, kind: e.target.value })}>
                  <option value="lumpSum">Lump sum ($MM net)</option>
                  <option value="perWell">$/well (gross)</option>
                </select>{' '}
                <input type="number" min="0" value={dealForm.value} placeholder="amount" onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} />{' '}
                <input type="text" value={dealForm.provenance || ''} placeholder="source / provenance" onChange={(e) => setDealForm({ ...dealForm, provenance: e.target.value })} />{' '}
                <button
                  type="button"
                  disabled={busy || !(Number(dealForm.value) >= 0) || dealForm.value === '' || !dealForm.provenance}
                  onClick={() => save(() => aroApi.setConfig(dealId, {
                    mode: 'd2d',
                    dealOverride: dealForm.kind === 'lumpSum'
                      ? { kind: 'lumpSum', amountMM: Number(dealForm.value), provenance: dealForm.provenance }
                      : { kind: 'perWell', amount: Number(dealForm.value), provenance: dealForm.provenance },
                  }))}
                >Apply</button>
              </span>
            )}
          </div>

          <h4>Assumptions (org deck)</h4>
          <div className="abs-table-wrap">
            <table className="abs-table" data-testid="aro-assumptions">
              <thead><tr><th>Assumption</th><th>Value</th><th>Unit</th><th>Source</th></tr></thead>
              <tbody>
                {r.assumptions.map((a) => (
                  <tr key={a.key} className={a.status === 'placeholder - set me' ? 'aro-ph-row' : ''}>
                    <td>{a.label}</td>
                    <td>{a.value == null ? '—' : typeof a.value === 'number' ? a.value.toLocaleString() : a.value}</td>
                    <td>{a.unit}</td>
                    <td>
                      {a.sourceRef ? <a href={a.sourceRef.url} target="_blank" rel="noreferrer" title={a.sourceRef.note}>{a.sourceRef.title}</a> : a.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4>Per well</h4>
          <div className="abs-table-wrap">
            <table className="abs-table" data-testid="aro-wells">
              <thead><tr><th>Well</th><th>Cat</th><th>St</th><th>Gross</th><th>WI</th><th>Net</th><th>P&amp;A yr</th><th>PV</th><th>Source</th><th /></tr></thead>
              <tbody>
                {r.wells.map((w) => {
                  const ov = view.wellOverrides?.[w.wellId];
                  return (
                    <tr key={w.wellId}>
                      <td title={w.flags?.join(', ')}>{w.lease || w.wellId}</td>
                      <td>{w.resCat}</td>
                      <td>{w.state || '—'}</td>
                      <td>{usd(w.grossCost)}</td>
                      <td>{(w.wi * 100).toFixed(1)}%</td>
                      <td>{usd(w.netCostAtPa)}</td>
                      <td>{w.paYear ?? '—'}</td>
                      <td>{usd(w.pv)}</td>
                      <td>
                        <SourceTag label={w.sourceLabel} />
                        {ov && <span className="aw-small"> {ov.provenance}{ov.quoteDate ? ` (${ov.quoteDate})` : ''} · {ov.setBy} {ov.setAt}</span>}
                      </td>
                      <td>
                        {!w.group && (ov ? (
                          <button type="button" disabled={busy} onClick={() => save(() => aroApi.clearWell(dealId, w.wellId))}>Clear</button>
                        ) : (
                          <button type="button" disabled={busy} onClick={() => setWellForm({ wellId: w.wellId, cost: '', source: 'quote', provenance: '', quoteDate: '' })}>Override</button>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {wellForm && (
            <div className="aro-wellform" data-testid="aro-wellform">
              <b>{wellForm.wellId}</b>{' '}
              <input type="number" min="0" placeholder="gross $" value={wellForm.cost} onChange={(e) => setWellForm({ ...wellForm, cost: e.target.value })} />{' '}
              <select value={wellForm.source} onChange={(e) => setWellForm({ ...wellForm, source: e.target.value })}>
                <option value="quote">Vendor quote</option>
                <option value="seller">Seller</option>
                <option value="user">User override</option>
              </select>{' '}
              <input type="text" placeholder="provenance (doc, vendor)" value={wellForm.provenance} onChange={(e) => setWellForm({ ...wellForm, provenance: e.target.value })} />{' '}
              <input type="date" value={wellForm.quoteDate} onChange={(e) => setWellForm({ ...wellForm, quoteDate: e.target.value })} />{' '}
              <button
                type="button"
                disabled={busy || wellForm.cost === '' || !wellForm.provenance}
                onClick={() => save(async () => {
                  const v = await aroApi.setWell(dealId, wellForm.wellId, {
                    cost: Number(wellForm.cost), source: wellForm.source, provenance: wellForm.provenance, quoteDate: wellForm.quoteDate || null,
                  });
                  setWellForm(null);
                  return v;
                })}
              >Save</button>{' '}
              <button type="button" onClick={() => setWellForm(null)}>Cancel</button>
            </div>
          )}

          <h4>Audit trail</h4>
          <ul className="aw-small" data-testid="aro-audit">
            {(view.audit || []).length === 0 && <li>No changes yet — values are the org D2D deck.</li>}
            {(view.audit || []).slice(0, 25).map((a, i) => (
              <li key={i}>{a.at} · {a.user_name} · {a.scope}{a.item_key ? ` ${a.item_key}` : ''}: {a.new ? JSON.stringify(a.new) : 'cleared'}</li>
            ))}
          </ul>
        </>
      )}
      {err && <div className="abs-aro-banner" role="status">{err}</div>}
    </section>
  );
}
